import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

/** 跟随全站 i18n：'ja' | 'zh' */
type Lang = 'ja' | 'zh';
type LJ = { ja: string; zh: string };

const copy = {
  title: { ja: '特定商取引法に基づく表示', zh: '基于特定商取引法的标示' } as LJ,
  updated_at: { ja: '最終更新日', zh: '最后更新' } as LJ,
  labels: {
    seller: { ja: '販売業者', zh: '销售业者' } as LJ,
    representative: { ja: '代表責任者', zh: '代表负责人' } as LJ,
    address: { ja: '所在地', zh: '所在地址' } as LJ,
    phone: { ja: '電話番号', zh: '电话号码' } as LJ,
    email: { ja: 'メールアドレス', zh: '邮箱' } as LJ,
    price: { ja: '販売価格', zh: '销售价格' } as LJ,
    extraFees: { ja: '商品代金以外の必要料金', zh: '商品以外费用' } as LJ,
    paymentMethods: { ja: '支払方法', zh: '支付方式' } as LJ,
    paymentTiming: { ja: '支払時期', zh: '支付时间' } as LJ,
    serviceTiming: { ja: '提供時期', zh: '提供时间' } as LJ,
    returnCancel: { ja: '返品・キャンセル', zh: '退货・取消' } as LJ,
    partnerTitle: { ja: '連携医療機関', zh: '连携医院' } as LJ,
    hospitalName: { ja: '医療機関名', zh: '医疗机构名' } as LJ,
    hospitalAddr: { ja: '所在地', zh: '所在地址' } as LJ,
    hospitalTel: { ja: '電話番号', zh: '电话号码' } as LJ,
    hospitalDoctor: { ja: '管理医師', zh: '管理医师' } as LJ,
    partnerNote: {
      ja: '※ 上記は連携機関の一例です。実際の連携先は診療内容により異なる場合があります。',
      zh: '※ 上述为连携机构示例。实际连携对象可能会因诊疗内容而有所不同。',
    } as LJ,
  },
  values: {
    seller: {
      ja: 'SMHC株式会社',
      zh: 'SMHC株式会社',
    } as LJ,
    representative: { ja: '李 鉉道', zh: '李 鉉道' } as LJ,
    address: {
      ja: '〒160-0023 東京都新宿区西新宿7-21-1 新宿ロイヤルビル201',
      zh: '〒160-0023 日本东京都新宿区西新宿7-21-1 新宿ロイヤルビル201',
    } as LJ,
    phone: { ja: '03-6908-6228', zh: '03-6908-6228' } as LJ,
    email: { ja: 'online-clinic@smhc.co.jp', zh: 'online-clinic@smhc.co.jp' } as LJ,

    price: {
      ja: '各サービスページに記載の価格（税込）をご参照ください。',
      zh: '请参照各服务页面所列价格（含税）。',
    } as LJ,
    extraFees: {
      ja: '消費税、振込手数料等が別途発生する場合があります。',
      zh: '可能会产生消费税、转账手续费等额外费用。',
    } as LJ,
    paymentMethods: {
      ja: 'クレジットカード決済、Alipay、WeChat Pay等の電子決済、その他当院が定める方法。',
      zh: '支持信用卡支付、支付宝（Alipay）、微信支付（WeChat Pay）等电子支付，及本院规定的其他方式。',
    } as LJ,
    paymentTiming: {
      ja: '各決済方法に基づき異なります。',
      zh: '依不同支付方式而异。',
    } as LJ,
    serviceTiming: {
      ja: 'ご予約確定後、診療日時にサービスを提供いたします。',
      zh: '预约确认后，于就诊时间提供服务。',
    } as LJ,
    returnCancel: {
      ja: 'サービスの性質上、提供後の返品・返金はできません。予約日時の変更やキャンセルは所定の規約に従います。',
      zh: '由于服务性质，提供后无法退货或退款。预约时间的更改与取消须遵循相关规定。',
    } as LJ,

    // 連携医療機関（示例值，替换为真实信息）
    hospitalName: { ja: '大日向医院', zh: '大日向医院' } as LJ,
    hospitalAddr: { ja: '〒160-0023 東京都新宿区西新宿1-1-1（例）', zh: '〒160-0023 日本东京都新宿区西新宿1-1-1（示例）' } as LJ,
    hospitalTel: { ja: '03-0000-0000（例）', zh: '03-0000-0000（示例）' } as LJ,
    hospitalDoc: { ja: '大日向 玲紀', zh: '大日向 玲紀' } as LJ,
  },
};

/** 小工具：返回当前语言文本 */
const L = (x: LJ, lang: Lang) => x[lang];

/** 两栏行布局组件 */
const Row: React.FC<{ label: string; children: React.ReactNode; withBorder?: boolean }> = ({
  label,
  children,
  withBorder = true,
}) => (
  <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 py-4 ${withBorder ? 'border-b border-slate-100' : ''}`}>
    <div className="text-sm font-semibold text-slate-600">{label}</div>
    <div className="sm:col-span-2 text-slate-800 leading-relaxed">{children}</div>
  </div>
);

const LegalPage: React.FC = () => {
  const { i18n } = useTranslation();
  const lang: Lang = useMemo(() => (i18n.language?.toLowerCase().startsWith('zh') ? 'zh' : 'ja'), [i18n.language]);

  return (
    <div className="pt-0 sm:pt-14 pb-10 sm:pb-20 bg-gray-50 font-sans">
      <Helmet>
        <title>{L(copy.title, lang)} - Melodis Clinic</title>
      </Helmet>

      <div className="container mx-auto px-0 sm:px-4 max-w-4xl">
        <div className="bg-white shadow-none sm:shadow-md rounded-none sm:rounded-3xl p-5 sm:p-8 md:p-12 border-y sm:border border-slate-100">
          {/* 标题（仅单语） */}
          <h1 className="text-xl md:text-2xl font-semibold text-slate-800 mb-8">{L(copy.title, lang)}</h1>

          <div className="space-y-10">
            {/* 基本信息 */}
            <section>
              <Row label={L(copy.labels.seller, lang)}>{L(copy.values.seller, lang)}</Row>
              <Row label={L(copy.labels.representative, lang)}>{L(copy.values.representative, lang)}</Row>
              <Row label={L(copy.labels.address, lang)}>{L(copy.values.address, lang)}</Row>
              <Row label={L(copy.labels.phone, lang)}>{L(copy.values.phone, lang)}</Row>
              <Row label={L(copy.labels.email, lang)} withBorder={false}>
                {L(copy.values.email, lang)}
              </Row>
            </section>

            {/* 連携医療機関（单语） */}
            <section>
              <h2 className="text-base font-semibold text-slate-800 mb-4">{L(copy.labels.partnerTitle, lang)}</h2>

              <div className="rounded-2xl border border-slate-200 bg-slate-50">
                <div className="p-6 md:p-7">
                  <Row label={L(copy.labels.hospitalName, lang)}>{L(copy.values.hospitalName, lang)}</Row>
                  {/* <Row label={L(copy.labels.hospitalAddr, lang)}>{L(copy.values.hospitalAddr, lang)}</Row>
                  <Row label={L(copy.labels.hospitalTel, lang)}>{L(copy.values.hospitalTel, lang)}</Row> */}
                  <Row label={L(copy.labels.hospitalDoctor, lang)} withBorder={false}>
                    {L(copy.values.hospitalDoc, lang)}
                  </Row>
                </div>
              </div>

              <p className="text-xs text-slate-500 mt-2">{L(copy.labels.partnerNote, lang)}</p>
            </section>

            {/* 价格与附加费用 */}
            <section>
              <Row label={L(copy.labels.price, lang)}>{L(copy.values.price, lang)}</Row>
              <Row label={L(copy.labels.extraFees, lang)} withBorder={false}>
                {L(copy.values.extraFees, lang)}
              </Row>
            </section>

            {/* 支付与时机 */}
            <section>
              <Row label={L(copy.labels.paymentMethods, lang)}>{L(copy.values.paymentMethods, lang)}</Row>
              <Row label={L(copy.labels.paymentTiming, lang)} withBorder={false}>
                {L(copy.values.paymentTiming, lang)}
              </Row>
            </section>

            {/* 提供时期与取消 */}
            <section>
              <Row label={L(copy.labels.serviceTiming, lang)}>{L(copy.values.serviceTiming, lang)}</Row>
              <Row label={L(copy.labels.returnCancel, lang)} withBorder={false}>
                {L(copy.values.returnCancel, lang)}
              </Row>
            </section>
          </div>

          {/* 更新时间（单语） */}
          <div className="mt-10 text-xs text-slate-500 text-center">
            {L(copy.updated_at, lang)}：2025-10-28
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
